import { __decorate, __param } from '../../../../../../external/tslib/tslib.es6.js';
import { URI } from 'monaco-editor/esm/vs/base/common/uri.js';
import { Emitter, Event } from 'monaco-editor/esm/vs/base/common/event.js';
import { DisposableMap, DisposableStore, dispose, combinedDisposable } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import { ISCMService, ISCMViewService } from 'vscode/vscode/vs/workbench/contrib/scm/common/scm';
import { MainContext, ExtHostContext } from 'vscode/vscode/vs/workbench/api/common/extHost.protocol';
import { extHostNamedCustomer } from '../../services/extensions/common/extHostCustomers.js';
import { CancellationToken } from 'monaco-editor/esm/vs/base/common/cancellation.js';
import { ThemeIcon } from 'monaco-editor/esm/vs/base/common/themables.js';
import { IQuickDiffService } from 'vscode/vscode/vs/workbench/contrib/scm/common/quickDiff';
import { ResourceTree } from 'vscode/vscode/vs/base/common/resourceTree';
import { IUriIdentityService } from 'monaco-editor/esm/vs/platform/uriIdentity/common/uriIdentity.js';
import { Codicon } from 'monaco-editor/esm/vs/base/common/codicons.js';
import { IWorkspaceContextService } from 'monaco-editor/esm/vs/platform/workspace/common/workspace.js';
import { basename } from 'monaco-editor/esm/vs/base/common/resources.js';

function getSCMInputBoxActionButtonIcon(actionButton) {
    if (!actionButton.icon) {
        return undefined;
    }
    else if (URI.isUri(actionButton.icon)) {
        return URI.revive(actionButton.icon);
    }
    else if (ThemeIcon.isThemeIcon(actionButton.icon)) {
        return actionButton.icon;
    }
    else {
        const icon = actionButton.icon;
        return { light: URI.revive(icon.light), dark: URI.revive(icon.dark) };
    }
}
function getIconFromIconDto(iconDto) {
    if (iconDto === undefined) {
        return undefined;
    }
    else if (URI.isUri(iconDto)) {
        return URI.revive(iconDto);
    }
    else if (ThemeIcon.isThemeIcon(iconDto)) {
        return iconDto;
    }
    else {
        const icon = iconDto;
        return { light: URI.revive(icon.light), dark: URI.revive(icon.dark) };
    }
}
class MainThreadSCMResourceGroup {
    get resourceTree() {
        if (!this._resourceTree) {
            const rootUri = this.provider.rootUri ?? URI.file('/');
            this._resourceTree = ( new ResourceTree(this, rootUri, this._uriIdentService.extUri));
            for (const resource of this.resources) {
                this._resourceTree.add(resource.sourceUri, resource);
            }
        }
        return this._resourceTree;
    }
    get hideWhenEmpty() { return !!this.features.hideWhenEmpty; }
    constructor(sourceControlHandle, handle, provider, features, label, id, _uriIdentService) {
        this.sourceControlHandle = sourceControlHandle;
        this.handle = handle;
        this.provider = provider;
        this.features = features;
        this.label = label;
        this.id = id;
        this._uriIdentService = _uriIdentService;
        this.resources = [];
        this._onDidChange = ( new Emitter());
        this.onDidChange = this._onDidChange.event;
        this._onDidChangeResources = ( new Emitter());
        this.onDidChangeResources = this._onDidChangeResources.event;
    }
    toJSON() {
        return {
            $mid: 4 ,
            sourceControlHandle: this.sourceControlHandle,
            groupHandle: this.handle
        };
    }
    splice(start, deleteCount, toInsert) {
        this.resources.splice(start, deleteCount, ...toInsert);
        this._resourceTree = undefined;
        this._onDidChangeResources.fire();
    }
    $updateGroup(features) {
        this.features = { ...this.features, ...features };
        this._onDidChange.fire();
    }
    $updateGroupLabel(label) {
        this.label = label;
        this._onDidChange.fire();
    }
}
class MainThreadSCMResource {
    constructor(proxy, sourceControlHandle, groupHandle, handle, sourceUri, resourceGroup, decorations, contextValue, command) {
        this.proxy = proxy;
        this.sourceControlHandle = sourceControlHandle;
        this.groupHandle = groupHandle;
        this.handle = handle;
        this.sourceUri = sourceUri;
        this.resourceGroup = resourceGroup;
        this.decorations = decorations;
        this.contextValue = contextValue;
        this.command = command;
    }
    open(preserveFocus) {
        return this.proxy.$executeResourceCommand(this.sourceControlHandle, this.groupHandle, this.handle, preserveFocus);
    }
    toJSON() {
        return {
            $mid: 3 ,
            sourceControlHandle: this.sourceControlHandle,
            groupHandle: this.groupHandle,
            handle: this.handle
        };
    }
}
class MainThreadSCMHistoryProvider {
    get actionButton() { return this._actionButton; }
    set actionButton(actionButton) {
        this._actionButton = actionButton;
        this._onDidChangeActionButton.fire();
    }
    get currentHistoryItemGroup() { return this._currentHistoryItemGroup; }
    set currentHistoryItemGroup(historyItemGroup) {
        this._currentHistoryItemGroup = historyItemGroup;
        this._onDidChangeCurrentHistoryItemGroup.fire();
    }
    constructor(proxy, handle) {
        this.proxy = proxy;
        this.handle = handle;
        this._onDidChangeActionButton = ( new Emitter());
        this.onDidChangeActionButton = this._onDidChangeActionButton.event;
        this._onDidChangeCurrentHistoryItemGroup = ( new Emitter());
        this.onDidChangeCurrentHistoryItemGroup = this._onDidChangeCurrentHistoryItemGroup.event;
    }
    async resolveHistoryItemGroupDetails(historyItemGroup) {
        const historyItemGroupBase = await this.resolveHistoryItemGroupBase(historyItemGroup.id);
        const ancestor = historyItemGroupBase ?
            await this.resolveHistoryItemGroupCommonAncestor(historyItemGroup.id, historyItemGroupBase.id) : undefined;
        let incoming;
        if (historyItemGroupBase) {
            incoming = {
                id: historyItemGroupBase.id,
                label: historyItemGroupBase.label,
                icon: Codicon.arrowCircleDown,
                ancestor: ancestor?.id,
                count: ancestor?.behind ?? 0,
            };
        }
        const outgoing = {
            id: historyItemGroup.id,
            label: historyItemGroup.label,
            icon: Codicon.arrowCircleUp,
            ancestor: ancestor?.id,
            count: ancestor?.ahead ?? 0,
        };
        return { incoming, outgoing };
    }
    async resolveHistoryItemGroupBase(historyItemGroupId) {
        return this.proxy.$resolveHistoryItemGroupBase(this.handle, historyItemGroupId, CancellationToken.None);
    }
    async resolveHistoryItemGroupCommonAncestor(historyItemGroupId1, historyItemGroupId2) {
        return this.proxy.$resolveHistoryItemGroupCommonAncestor(this.handle, historyItemGroupId1, historyItemGroupId2, CancellationToken.None);
    }
    async provideHistoryItems(historyItemGroupId, options) {
        const historyItems = await this.proxy.$provideHistoryItems(this.handle, historyItemGroupId, options, CancellationToken.None);
        return historyItems?.map(historyItem => ({ ...historyItem, icon: getIconFromIconDto(historyItem.icon) }));
    }
    async provideHistoryItemChanges(historyItemId) {
        const changes = await this.proxy.$provideHistoryItemChanges(this.handle, historyItemId, CancellationToken.None);
        return changes?.map(change => ({
            uri: URI.revive(change.uri),
            originalUri: change.originalUri && URI.revive(change.originalUri),
            modifiedUri: change.modifiedUri && URI.revive(change.modifiedUri),
            renameUri: change.renameUri && URI.revive(change.renameUri)
        }));
    }
}
class MainThreadSCMProvider {
    static { this.ID_HANDLE = 0; }
    get id() { return this._id; }
    get handle() { return this._handle; }
    get label() { return this._label; }
    get rootUri() { return this._rootUri; }
    get inputBoxDocumentUri() { return this._inputBoxDocumentUri; }
    get contextValue() { return this._providerId; }
    get commitTemplate() { return this.features.commitTemplate || ''; }
    get historyProvider() { return this._historyProvider; }
    get acceptInputCommand() { return this.features.acceptInputCommand; }
    get actionButton() { return this.features.actionButton ?? undefined; }
    get statusBarCommands() { return this.features.statusBarCommands; }
    get count() { return this.features.count; }
    get name() { return this._name ?? this._label; }
    get onDidChangeStatusBarCommands() { return this._onDidChangeStatusBarCommands.event; }
    constructor(proxy, _handle, _providerId, _label, _rootUri, _inputBoxDocumentUri, _quickDiffService, _uriIdentService, _workspaceContextService) {
        this.proxy = proxy;
        this._handle = _handle;
        this._providerId = _providerId;
        this._label = _label;
        this._rootUri = _rootUri;
        this._inputBoxDocumentUri = _inputBoxDocumentUri;
        this._quickDiffService = _quickDiffService;
        this._uriIdentService = _uriIdentService;
        this._workspaceContextService = _workspaceContextService;
        this._id = `scm${MainThreadSCMProvider.ID_HANDLE++}`;
        this.groups = [];
        this._onDidChangeResourceGroups = ( new Emitter());
        this.onDidChangeResourceGroups = this._onDidChangeResourceGroups.event;
        this._onDidChangeResources = ( new Emitter());
        this.onDidChangeResources = this._onDidChangeResources.event;
        this._groupsByHandle = Object.create(null);
        this.features = {};
        this._onDidChangeCommitTemplate = ( new Emitter());
        this.onDidChangeCommitTemplate = this._onDidChangeCommitTemplate.event;
        this._onDidChangeStatusBarCommands = ( new Emitter());
        this._onDidChangeHistoryProvider = ( new Emitter());
        this.onDidChangeHistoryProvider = this._onDidChangeHistoryProvider.event;
        this._onDidChange = ( new Emitter());
        this.onDidChange = this._onDidChange.event;
        this.isSCM = true;
        if (_rootUri) {
            const folder = this._workspaceContextService.getWorkspaceFolder(_rootUri);
            if (folder?.uri.toString() === ( _rootUri.toString())) {
                this._name = folder.name;
            }
            else if (_rootUri.path !== '/') {
                this._name = basename(_rootUri);
            }
        }
    }
    $updateSourceControl(features) {
        this.features = { ...this.features, ...features };
        this._onDidChange.fire();
        if (typeof features.commitTemplate !== 'undefined') {
            this._onDidChangeCommitTemplate.fire(this.commitTemplate);
        }
        if (typeof features.statusBarCommands !== 'undefined') {
            this._onDidChangeStatusBarCommands.fire(this.statusBarCommands);
        }
        if (features.hasQuickDiffProvider && !this._quickDiff) {
            this._quickDiff = this._quickDiffService.addQuickDiffProvider({
                label: features.quickDiffLabel ?? this.label,
                rootUri: this.rootUri,
                isSCM: this.isSCM,
                getOriginalResource: (uri) => this.getOriginalResource(uri)
            });
        }
        else if (features.hasQuickDiffProvider === false && this._quickDiff) {
            this._quickDiff.dispose();
            this._quickDiff = undefined;
        }
        if (features.hasHistoryProvider && !this._historyProvider) {
            this._historyProvider = ( new MainThreadSCMHistoryProvider(this.proxy, this.handle));
            this._onDidChangeHistoryProvider.fire();
        }
        else if (features.hasHistoryProvider === false && this._historyProvider) {
            this._historyProvider = undefined;
            this._onDidChangeHistoryProvider.fire();
        }
    }
    $registerGroups(_groups) {
        const groups = ( _groups.map(([handle, id, label, features]) => {
            const group = ( new MainThreadSCMResourceGroup(this.handle, handle, this, features, label, id, this._uriIdentService));
            this._groupsByHandle[handle] = group;
            return group;
        }));
        this.groups.splice(this.groups.length, 0, ...groups);
        this._onDidChangeResourceGroups.fire();
    }
    $updateGroup(handle, features) {
        const group = this._groupsByHandle[handle];
        if (!group) {
            return;
        }
        group.$updateGroup(features);
    }
    $updateGroupLabel(handle, label) {
        const group = this._groupsByHandle[handle];
        if (!group) {
            return;
        }
        group.$updateGroupLabel(label);
    }
    $spliceGroupResourceStates(splices) {
        for (const [groupHandle, groupSlices] of splices) {
            const group = this._groupsByHandle[groupHandle];
            if (!group) {
                console.warn(`SCM group ${groupHandle} not found in provider ${this.label}`);
                continue;
            }
            groupSlices.reverse();
            for (const [start, deleteCount, rawResources] of groupSlices) {
                const resources = ( rawResources.map(rawResource => {
                    const [handle, sourceUri, icons, tooltip, strikeThrough, faded, contextValue, command] = rawResource;
                    const [light, dark] = icons;
                    const icon = ThemeIcon.isThemeIcon(light) ? light : URI.revive(light);
                    const iconDark = (ThemeIcon.isThemeIcon(dark) ? dark : URI.revive(dark)) || icon;
                    const decorations = {
                        icon: icon,
                        iconDark: iconDark,
                        tooltip,
                        strikeThrough,
                        faded
                    };
                    return ( new MainThreadSCMResource(
                        this.proxy,
                        this.handle,
                        groupHandle,
                        handle,
                        URI.revive(sourceUri),
                        group,
                        decorations,
                        contextValue || undefined,
                        command
                    ));
                }));
                group.splice(start, deleteCount, resources);
            }
        }
        this._onDidChangeResources.fire();
    }
    $unregisterGroup(handle) {
        const group = this._groupsByHandle[handle];
        if (!group) {
            return;
        }
        delete this._groupsByHandle[handle];
        this.groups.splice(this.groups.indexOf(group), 1);
        this._onDidChangeResourceGroups.fire();
    }
    async getOriginalResource(uri) {
        if (!this.features.hasQuickDiffProvider) {
            return null;
        }
        const result = await this.proxy.$provideOriginalResource(this.handle, uri, CancellationToken.None);
        return result && URI.revive(result);
    }
    $onDidChangeHistoryProviderActionButton(actionButton) {
        if (!this._historyProvider) {
            return;
        }
        this._historyProvider.actionButton = actionButton ?? undefined;
    }
    $onDidChangeHistoryProviderCurrentHistoryItemGroup(currentHistoryItemGroup) {
        if (!this._historyProvider) {
            return;
        }
        this._historyProvider.currentHistoryItemGroup = currentHistoryItemGroup ?? undefined;
    }
    toJSON() {
        return {
            $mid: 5 ,
            handle: this.handle
        };
    }
    dispose() {
        this._quickDiff?.dispose();
    }
}
class MainThreadSCMInputBoxValueProvider {
    constructor(proxy, handle, label, icon) {
        this.proxy = proxy;
        this.handle = handle;
        this.label = label;
        this.icon = icon;
    }
    provideValue(rootUri, context, token) {
        return this.proxy.$provideInputBoxValue(this.handle, rootUri, context, token);
    }
}
let MainThreadSCM = class MainThreadSCM {
    constructor(extHostContext, scmService, scmViewService, quickDiffService, _uriIdentService, workspaceContextService) {
        this.scmService = scmService;
        this.scmViewService = scmViewService;
        this.quickDiffService = quickDiffService;
        this._uriIdentService = _uriIdentService;
        this.workspaceContextService = workspaceContextService;
        this._repositories = ( new Map());
        this._repositoryDisposables = ( new Map());
        this._inputBoxValueProviders = ( new DisposableMap());
        this._disposables = ( new DisposableStore());
        this._proxy = ( extHostContext.getProxy(ExtHostContext.ExtHostSCM));
    }
    dispose() {
        dispose(( this._repositories.values()));
        this._repositories.clear();
        dispose(( this._repositoryDisposables.values()));
        this._repositoryDisposables.clear();
        this._inputBoxValueProviders.dispose();
        this._disposables.dispose();
    }
    $registerSourceControl(handle, id, label, rootUri, inputBoxDocumentUri) {
        const provider = ( new MainThreadSCMProvider(
            this._proxy,
            handle,
            id,
            label,
            rootUri ? URI.revive(rootUri) : undefined,
            URI.revive(inputBoxDocumentUri),
            this.quickDiffService,
            this._uriIdentService,
            this.workspaceContextService
        ));
        const repository = this.scmService.registerSCMProvider(provider);
        this._repositories.set(handle, repository);
        const disposable = combinedDisposable(Event.filter(this.scmViewService.onDidFocusRepository, r => r === repository)(_ => this._proxy.$setSelectedSourceControl(handle)), repository.input.onDidChange(({ value }) => this._proxy.$onInputBoxValueChange(handle, value)));
        if (this.scmViewService.focusedRepository === repository) {
            setTimeout(() => this._proxy.$setSelectedSourceControl(handle), 0);
        }
        if (repository.input.value) {
            setTimeout(() => this._proxy.$onInputBoxValueChange(handle, repository.input.value), 0);
        }
        this._repositoryDisposables.set(handle, disposable);
    }
    $updateSourceControl(handle, features) {
        const repository = this._repositories.get(handle);
        if (!repository) {
            return;
        }
        const provider = repository.provider;
        provider.$updateSourceControl(features);
    }
    $unregisterSourceControl(handle) {
        const repository = this._repositories.get(handle);
        if (!repository) {
            return;
        }
        this._repositoryDisposables.get(handle).dispose();
        this._repositoryDisposables.delete(handle);
        repository.dispose();
        this._repositories.delete(handle);
    }
    $registerSourceControlInputBoxValueProvider(handle, sourceControlId, label, icon) {
        const provider = ( new MainThreadSCMInputBoxValueProvider(this._proxy, handle, label, getIconFromIconDto(icon)));
        const disposable = this.scmService.registerSCMInputValueProvider(sourceControlId, provider);
        this._inputBoxValueProviders.set(handle, disposable);
    }
    $unregisterSourceControlInputBoxValueProvider(handle) {
        const provider = this._inputBoxValueProviders.get(handle);
        if (!provider) {
            return;
        }
        provider.dispose();
        this._inputBoxValueProviders.deleteAndDispose(handle);
    }
    $registerGroups(sourceControlHandle, groups, splices) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        const provider = repository.provider;
        provider.$registerGroups(groups);
        provider.$spliceGroupResourceStates(splices);
    }
    $updateGroup(sourceControlHandle, groupHandle, features) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        const provider = repository.provider;
        provider.$updateGroup(groupHandle, features);
    }
    $updateGroupLabel(sourceControlHandle, groupHandle, label) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        const provider = repository.provider;
        provider.$updateGroupLabel(groupHandle, label);
    }
    $spliceResourceStates(sourceControlHandle, splices) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        const provider = repository.provider;
        provider.$spliceGroupResourceStates(splices);
    }
    $unregisterGroup(sourceControlHandle, handle) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        const provider = repository.provider;
        provider.$unregisterGroup(handle);
    }
    $setInputBoxValue(sourceControlHandle, value) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        repository.input.setValue(value, false);
    }
    $setInputBoxPlaceholder(sourceControlHandle, placeholder) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        repository.input.placeholder = placeholder;
    }
    $setInputBoxEnablement(sourceControlHandle, enabled) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        repository.input.enabled = enabled;
    }
    $setInputBoxVisibility(sourceControlHandle, visible) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        repository.input.visible = visible;
    }
    $setInputBoxActionButton(sourceControlHandle, actionButton) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        repository.input.actionButton = actionButton ? { ...actionButton, icon: getSCMInputBoxActionButtonIcon(actionButton) } : undefined;
    }
    $showValidationMessage(sourceControlHandle, message, type) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        repository.input.showValidationMessage(message, type);
    }
    $setValidationProviderIsEnabled(sourceControlHandle, enabled) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        if (enabled) {
            repository.input.validateInput = async (value, pos) => {
                const result = await this._proxy.$validateInput(sourceControlHandle, value, pos);
                return result && { message: result[0], type: result[1] };
            };
        }
        else {
            repository.input.validateInput = async () => undefined;
        }
    }
    $onDidChangeHistoryProviderActionButton(sourceControlHandle, actionButton) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        const provider = repository.provider;
        provider.$onDidChangeHistoryProviderActionButton(actionButton);
    }
    $onDidChangeHistoryProviderCurrentHistoryItemGroup(sourceControlHandle, historyItemGroup) {
        const repository = this._repositories.get(sourceControlHandle);
        if (!repository) {
            return;
        }
        const provider = repository.provider;
        provider.$onDidChangeHistoryProviderCurrentHistoryItemGroup(historyItemGroup);
    }
};
MainThreadSCM = __decorate([
    extHostNamedCustomer(MainContext.MainThreadSCM),
    ( __param(1, ISCMService)),
    ( __param(2, ISCMViewService)),
    ( __param(3, IQuickDiffService)),
    ( __param(4, IUriIdentityService)),
    ( __param(5, IWorkspaceContextService))
], MainThreadSCM);

export { MainThreadSCM };
